import { Download, Edit } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationStatus } from 'src/enums/application-status.enum';
import { Region, Speciality } from '.';
import { Commission } from '../../commissions';
import { CRUD_TYPE, _Locale } from '../../commissions/BasicTable';
import InputFileUpload from './InputFile';
// import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputMask from 'react-input-mask';
import { onDownload } from 'src/common/utils/helpers';
import { dateFormat } from 'src/hooks';
import { ApiApplication } from 'src/http';

export interface User {
  middleName: string;
  lastName: string;
  firstName: string;
  dob: string;
  pin: string;
  passportSeries: string;
  passportNumber: string;
  passportIssueDate: string;
  passportIssuePlace: string;
  passportExpireDate: string;
  gender: string;
  phone: string;
  email: string;
  id?: string | number;
}
export interface Application {
  id?: string | number;
  appType?: string | number;
  workplace: string;
  position: string;
  specialty: string;
  appFrom: string;
  // hisSpecialty: string;
  invoice: string | null;
  // nameMedicalEducational: string;
  qualificationCategory: string;
  qualificationCategorySpecialty: string;
  qualificationCategoryIssued: string;
  creditAccumulated: string;
  workExperienceYear: number | string;
  specialtyWorkExperience: number | string;
  claimedQualification: string;
  // presentation: string | null;
  // files
  copyDiploma: string;
  copyDocTraining: string | null;
  electronicWorkBook: string;
  copyQualificationCategory: string;
  copyCertificateQualification: string;
  // files
  commission: string;
  specialization: string;
  specializationId?: string;
  commissionId?: string;
  regionId?: string;
  region: string;
  reason: string;
  documentWork: string | null;

  absentReason?: null | string;
  attestationDate?: null | string;
  attestationLocation?: null | string;
  confirmedAt?: null | string;
  confirmedBy?: null | string;
  correctAnswersCount?: null | string;
  createdAt?: null | string;
  declineReason?: null | string;
  declinedAt?: null | string;
  declinedBy?: null | string;
  direction?: null | string;
  district?: null | string;
  indPhone: string;
  isDetermineByMe?: null | string;
  isPassed?: null | string;
  isWarned?: null | string;
  language?: null | string;
  prevUid?: null | string;
  questions?: null | string;
  questionsCount?: null | string;
  score?: null | string;
  stoppedReason?: null | string;
  taskId?: null | string;
  uid?: null | string;
  user?: null | User;
  status: string;
}

export const _defaultItem: Application = {
  id: 0,
  workplace: '',
  appType: '',
  position: '',
  specialty: '',
  appFrom: 'attestat_local',
  // hisSpecialty: '',
  // nameMedicalEducational: '',
  qualificationCategory: '0',
  qualificationCategoryIssued: '',
  qualificationCategorySpecialty: '',
  creditAccumulated: '',
  workExperienceYear: 0,
  specialtyWorkExperience: 0,
  claimedQualification: '1',
  // files
  copyDiploma: '',
  electronicWorkBook: '',
  copyQualificationCategory: '',
  copyCertificateQualification: '',
  // presentation: '',
  // files
  commission: '',
  specialization: '',
  region: '',
  status: ApplicationStatus.PENDING,
  invoice: '',
  indPhone: '',
  reason: '',
};

export const COLORS: any = {
  PENDING: 'secondary.main',
  PASSED: 'success.dark',
  FAILED: 'error.dark',
  REJECTED_FOR_REVIEW: 'error.light',
  APPLICATION_FOR_RECONSIDERATION: 'error.light',
  PERMISSION_TO_PAYMENT: 'success.light',
  // REJECTED_DUE_TO_NON_IMPLEMENTATION: 'error.light',
  PAYMENT_DONE: 'success.main',
  CONFIRMED: 'success.light',
  DECLINED: 'error.main',
};

export default function BasicTable({
  data,
  createItem,
  updateItem,
  commissions,
  specialities,
  regions,
  changePagination,
  uploadCheck,
  total,
}: {
  data: Application[] | null;
  createItem: Function;
  updateItem: Function;
  uploadCheck: Function;
  commissions: Commission[] | null;
  regions: Region[] | null;
  specialities: Speciality[] | null;
  changePagination: Function;
  total: number;
}) {
  const { t, i18n } = useTranslation();

  const [item, setItem] = useState<Application>({ ..._defaultItem });

  const [open, setOpen] = useState(false);
  const [crud, setCrud] = useState(CRUD_TYPE.CREATE);

  const handleClickOpen = (type: CRUD_TYPE, _item?: Application) => {
    if (CRUD_TYPE.UPDATE && _item) {
      setItem({ ..._item });
    } else {
      if (item.id !== 10000000000000000000) {
        setItem({ ..._defaultItem, id: 10000000000000000000 });
      }
    }
    setCrud(type);
    setOpen(true);
  };

  const handleCreate = () => {
    if (crud === CRUD_TYPE.CREATE) {
      createItem({ ...item });
    } else {
      updateItem({ ...item });
    }
    resetItem();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const resetItem = () => {
    setItem({ ..._defaultItem });
  };

  const getFileValue = ({
    file,
    field,
    fileName,
  }: {
    file: string;
    field: string;
    fileName: string;
  }) => {
    if (!file && !errorFiles.includes(field)) {
      setErrorFiles([...errorFiles, field]);
    }
    switch (field) {
      case 'copyDiploma':
        setItem({ ...item, copyDiploma: fileName });
        break;
      case 'copyQualificationCategory':
        setItem({ ...item, copyQualificationCategory: fileName });
        break;
      case 'copyCertificateQualification':
        setItem({ ...item, copyCertificateQualification: fileName });
        break;
      case 'electronicWorkBook':
        setItem({ ...item, electronicWorkBook: fileName });
        break;
      // case 'presentation':
      //   setItem({ ...item, presentation: fileName });
      //   break;
      case 'invoice':
        setItem({ ...item, invoice: fileName });
        sendInvoice(fileName);
        break;
      default:
        break;
    }
  };

  const sendInvoice = async (fileName: string) => {
    await ApiApplication.invoiceUpload(expanded, {
      invoice: fileName,
    });
    uploadCheck();
  };

  const checkFields = () => {
    return (
      item.appType === '' ||
      item.workplace === '' ||
      item.position === '' ||
      // item.specialty === '' ||
      // item.hisSpecialty === '' ||
      // item.nameMedicalEducational === '' ||
      item.indPhone === '' ||
      item.copyDiploma === null ||
      // item.qualificationCategory === '' ||
      // item.qualificationCategoryIssued === '' ||
      // item.qualificationCategorySpecialty === '' ||
      // item.copyQualificationCategory === null ||
      item.creditAccumulated === '' ||
      item.copyCertificateQualification === null ||
      item.workExperienceYear === 0 ||
      item.specialtyWorkExperience === 0 ||
      // item.claimedQualification === '1' ||
      item.electronicWorkBook === null ||
      item.commission === '' ||
      item.specialization === '' ||
      item.region === ''
    );
  };

  const [expanded, setExpanded] = useState<string>('');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : '');
  };

  useEffect(() => {
    checkFields();
  }, [item]);

  const pageChangeHandler = (event: any, pageNumber: string | number) => {
    changePagination(pageNumber);
  };

  // FILE VALIDATION
  const [errorFiles, setErrorFiles] = useState<string[]>([]);
  const checkFiles = () => {
    const fields = ['electronicWorkBook', 'copyCertificateQualification', 'copyDiploma'];
    let errors: string[] = [];
    fields.map((f) =>
      item[f as keyof Application] === null ? errors.push(f) : errors.splice(0, errors.indexOf(f))
    );
    setErrorFiles(errors);
  };
  useEffect(() => {
    checkFiles();
  }, [item.copyCertificateQualification, item.electronicWorkBook, item.copyDiploma]);

  const [_commissions, _setCommissions] = useState<Commission[] | undefined | null>(commissions);
  const checkAppType = () => {
    let temp = commissions?.filter((f) => Number(f.appType) === Number(item.appType));
    _setCommissions(temp);
  };
  useEffect(() => {
    checkAppType();
  }, [item.appType]);

  const [_specialities, _setSpecialities] = useState<Speciality[] | undefined | null>(specialities);
  const checkCommision = () => {
    let temp = specialities?.filter((f) => Number(f.commission) === Number(item.commission));
    _setSpecialities(temp);
  };
  useEffect(() => {
    checkCommision();
  }, [item.commission]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        sx={{
          mx: 'auto',
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            fontSize: 22,
            marginBottom: 2,
          }}
        >
          {t(crud === CRUD_TYPE.CREATE ? 'applications.apply' : 'attestations.edit')}
        </DialogTitle>
        <Box
          component="form"
          // noValidate
          autoComplete="off"
          onSubmit={(e: any) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              fontSize: 18,
              // overflowY: 'scroll',
            }}
          >
            <DialogContentText id="alert-dialog-slide-description"></DialogContentText>
            <Box p={2}>
              <Typography
                variant="body2"
                component="h5"
                fontWeight={'bold'}
                color={'red'}
                textTransform={'uppercase'}
                mb={1}
              >
                {t('Diqqat')}
              </Typography>
              <Typography variant="body2" component="h5">
                {t('SMS xabar olish uchun ishlab turgan telefon raqamingizni kiriting')}
              </Typography>
              <InputMask
                mask="(99)-999-99-99"
                value={item.indPhone}
                disabled={false}
                onChange={(e) => setItem({ ...item, indPhone: e.target.value })}
              >
                {() => <TextField variant="outlined" fullWidth />}
              </InputMask>
              {/* <InputMask mask="(99)-999-99-99" value={item.indPhone} disabled={false}>
                {() => (
                  <TextField variant="outlined" />
                  // <TextField
                  //   required
                  //   label={t('profile.phone')}
                  //   type="text"
                  //   sx={{
                  //     mt: 1,
                  //   }}
                  //   fullWidth
                  //   variant="outlined"
                  //   onChange={(e) => setItem({ ...item, indPhone: e.target.value })}
                  //   defaultValue={item.indPhone}
                  // />
                )}
              </InputMask> */}
            </Box>
            <Typography variant="subtitle1" component="h3" my={2}>
              I. {t('Малакасига оид маълумотлар')}
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Box borderRadius={2} border={'1px solid'} borderColor={'red'} p={2}>
                <Typography
                  variant="body2"
                  component="h5"
                  mb={2}
                  fontWeight={'bold'}
                  color={'red'}
                  textTransform={'uppercase'}
                >
                  {t('Diqqat')}&nbsp;&nbsp;&nbsp;{t("E'tibor bilan tanlang")}
                </Typography>
                <FormControl
                  sx={{
                    px: 2,
                  }}
                >
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    defaultValue="female"
                    name="radio-buttons-group"
                    value={item.appType}
                    onChange={(e) => setItem({ ...item, appType: e.target.value })}
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio required />}
                      label={t('Врачлар ва фармацевтлар')}
                      sx={{
                        mb: 1,
                      }}
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio required />}
                      label={t(
                        '“Олий ҳамширалик иши” мутахассислигига эга бўлган ҳамширалар, ўрта тиббиёт ва ўрта фармацевтика ходимлари'
                      )}
                    />
                    {/* <FormControlLabel
                      value="3"
                      control={<Radio required />}
                      label={t('Бирламчи тиббиёт ходимлари')}
                    /> */}
                  </RadioGroup>
                </FormControl>
              </Box>
              <TextField
                required
                label={t('workplace')}
                type="text"
                sx={{
                  mt: 3,
                }}
                fullWidth
                variant="outlined"
                onChange={(e) => setItem({ ...item, workplace: e.target.value })}
                defaultValue={item.workplace}
              />
              <TextField
                required
                label={t('position')}
                type="text"
                variant="outlined"
                fullWidth
                onChange={(e) => setItem({ ...item, position: e.target.value })}
                sx={{
                  mt: 3,
                }}
                defaultValue={item.position}
              />
              <FormControl
                sx={{
                  mt: 3,
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">{t('commissions')}</InputLabel>
                <Select
                  required
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={item.commission}
                  label="Age"
                  onChange={(e) => {
                    setItem({ ...item, commission: e.target.value });
                  }}
                >
                  {_commissions?.map((c: Commission) => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                sx={{
                  mt: 3,
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">{t('specialty')}</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  required
                  value={item.specialization}
                  label="Age"
                  onChange={(e) => {
                    setItem({ ...item, specialization: e.target.value });
                  }}
                >
                  {_specialities?.map((c: Speciality) => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('workExperienceYear')}
                type="number"
                variant="outlined"
                fullWidth
                required
                sx={{
                  mt: 3,
                }}
                onChange={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 2);
                  setItem({ ...item, workExperienceYear: e.target.value });
                }}
                defaultValue={item.workExperienceYear}
                inputProps={{ maxLength: 1 }}
              />
              <TextField
                label={t('specialtyWorkExperience')}
                type="number"
                variant="outlined"
                required
                fullWidth
                sx={{
                  mt: 3,
                }}
                onChange={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 2);
                  setItem({ ...item, specialtyWorkExperience: e.target.value });
                }}
                defaultValue={item.specialtyWorkExperience}
              />
              <FormControl
                sx={{
                  mt: 3,
                  mb: 2,
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">{t('claimedQualification')}</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  required
                  value={item.claimedQualification}
                  label="Age"
                  onChange={(e) => {
                    setItem({ ...item, claimedQualification: e.target.value });
                  }}
                >
                  <MenuItem value={'1'}>{t('Олий')}</MenuItem>
                  <MenuItem value={'2'}>{t('Биринчи')}</MenuItem>
                  <MenuItem value={'3'}>{t('Иккинчи')}</MenuItem>
                  <MenuItem value={'4'}>{t('Учинчи')}</MenuItem>
                </Select>
              </FormControl>
              {/* {errorFiles.includes('electronicWorkBook') ? (
              ) : null} */}
              <InputFileUpload
                getFileValue={getFileValue}
                field="electronicWorkBook"
                fileName={item.electronicWorkBook}
              />
              <input
                type="text"
                name="electronicWorkBook_check"
                required
                defaultValue={item.electronicWorkBook ? 'electronicWorkBook' : ''}
                style={{ opacity: 0, marginTop: '-20px', float: 'right' }}
              />
            </Box>
            <Typography variant="subtitle1" component="h3" my={2}>
              II. {t('Профессионал таълим ташкилотларида ўқиганлигини тасдиқловчи архив')}
            </Typography>
            <Box sx={{ pl: 2 }}>
              {/* <TextField
                label={t('hisSpecialty')}
                type="text"
                variant="outlined"
                required
                fullWidth
                sx={{
                  mt: 3,
                }}
                onChange={(e) => {
                  setItem({ ...item, hisSpecialty: e.target.value });
                }}
                defaultValue={item.hisSpecialty}
              /> */}
              {/* <TextField
                label={t('nameMedicalEducational')}
                type="text"
                variant="outlined"
                fullWidth
                required
                sx={{
                  mt: 3,
                }}
                onChange={(e) => {
                  setItem({ ...item, nameMedicalEducational: e.target.value });
                }}
                defaultValue={item.nameMedicalEducational}
              /> */}

              {/* {errorFiles.includes('copyDiploma') ? (
                <Typography color={'red'} fontSize={18} marginTop={3}>
                  {t('Please, upload pdf file')}
                </Typography>
              ) : null} */}
              <InputFileUpload
                getFileValue={getFileValue}
                field="copyDiploma"
                fileName={item.copyDiploma}
              />
              <input
                type="text"
                name="copyDiploma_check"
                required
                defaultValue={item.copyDiploma ? 'copyDiploma' : ''}
                style={{ opacity: 0, marginTop: '-20px', float: 'right' }}
              />
            </Box>
            <Typography variant="subtitle1" component="h3" my={2}>
              III. {t('Амалдаги малака тоифаси: (агар мавжуд бўлса)')}
            </Typography>
            <Box sx={{ pl: 2 }}>
              <FormControl
                sx={{
                  mt: 3,
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">{t('qualificationCategory')}</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={item.qualificationCategory}
                  label="Age"
                  onChange={(e) => {
                    setItem({ ...item, qualificationCategory: e.target.value });
                  }}
                >
                  <MenuItem value={'0'}>{t('attestation.absent')}</MenuItem>
                  <MenuItem value={'1'}>{t('Олий')}</MenuItem>
                  <MenuItem value={'2'}>{t('Биринчи')}</MenuItem>
                  <MenuItem value={'3'}>{t('Иккинчи')}</MenuItem>
                  <MenuItem value={'4'}>{t('Учинчи')}</MenuItem>
                  {/* <MenuItem value={'0'} disabled={Number(item.claimedQualification) !== 4}>
                    {t('attestation.absent')}
                  </MenuItem>
                  <MenuItem
                    value={'1'}
                    disabled={
                      !(
                        Number(item.claimedQualification) === 1 ||
                        Number(item.claimedQualification) === 2
                      )
                    }
                  >
                    {t('Олий')}
                  </MenuItem>
                  <MenuItem
                    value={'2'}
                    disabled={
                      !(
                        Number(item.claimedQualification) === 1 ||
                        Number(item.claimedQualification) === 2 ||
                        Number(item.claimedQualification) === 3
                      )
                    }
                  >
                    {t('Биринчи')}
                  </MenuItem>
                  <MenuItem
                    value={'3'}
                    disabled={
                      !(
                        Number(item.claimedQualification) === 2 ||
                        Number(item.claimedQualification) === 3 ||
                        Number(item.claimedQualification) === 4
                      )
                    }
                  >
                    {t('Иккинчи')}
                  </MenuItem>
                  <MenuItem value={'4'} disabled={!(Number(item.claimedQualification) >= 4)}>
                    {t('Учинчи')}
                  </MenuItem> */}
                </Select>
              </FormControl>
              {item.qualificationCategory !== '0' ? (
                <>
                  <TextField
                    label={t('qualificationCategoryIssued')}
                    type="date"
                    variant="outlined"
                    sx={{
                      mt: 3,
                    }}
                    InputProps={{ inputProps: { min: '1900-01-01' } }}
                    onChange={(e) => {
                      setItem({ ...item, qualificationCategoryIssued: e.target.value });
                    }}
                    defaultValue={item.qualificationCategoryIssued}
                  />
                  <TextField
                    label={t('qualificationCategorySpecialty')}
                    type="text"
                    variant="outlined"
                    fullWidth
                    sx={{
                      mt: 3,
                    }}
                    onChange={(e) => {
                      setItem({ ...item, qualificationCategorySpecialty: e.target.value });
                    }}
                    defaultValue={item.qualificationCategorySpecialty}
                  />
                  <InputFileUpload
                    getFileValue={getFileValue}
                    field="copyQualificationCategory"
                    fileName={item.copyQualificationCategory}
                  />
                </>
              ) : null}
              {/* <InputFileUpload
                getFileValue={getFileValue}
                field="presentation"
                fileName={item.presentation}
              /> */}
            </Box>
            <Typography variant="subtitle1" component="h3" mb={2} mt={5}>
              IV. {t('Кейинги 5 йил давомида малака оширганлиги')}
            </Typography>
            <Box sx={{ pl: 2 }}>
              <TextField
                label={t('creditAccumulated')}
                type="number"
                variant="outlined"
                fullWidth
                required
                sx={{
                  mt: 3,
                }}
                onChange={(e) => {
                  e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 4);
                  setItem({ ...item, creditAccumulated: e.target.value });
                }}
                defaultValue={item.creditAccumulated}
              />

              {/* {errorFiles.includes('copyCertificateQualification') ? (
                <Typography color={'red'} fontSize={18} marginTop={3}>
                  {t('Please, upload pdf file')}
                </Typography>
              ) : null} */}
              <InputFileUpload
                getFileValue={getFileValue}
                field="copyCertificateQualification"
                fileName={item.copyCertificateQualification}
              />
              <input
                type="text"
                name="copyCertificateQualification_check"
                required
                defaultValue={
                  item.copyCertificateQualification ? 'copyCertificateQualification' : ''
                }
                style={{ opacity: 0, marginTop: '-20px', float: 'right' }}
              />

              <FormControl
                sx={{
                  mt: 3,
                }}
                fullWidth
              >
                <InputLabel id="demo-simple-select-label">
                  {t('Талабгорнинг фаолият юритаётган худуди')}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={item.region}
                  required
                  label="Age"
                  onChange={(e) => {
                    setItem({ ...item, region: e.target.value });
                  }}
                >
                  {regions?.map((c: Region) => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.title[i18n.language as _Locale]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              justifyContent: 'space-between',
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            <Button onClick={handleClose} variant="outlined" size="large" color="secondary">
              {t('action.cancel')}
            </Button>
            {/* <button type="submit">{t('action.save')}</button> */}
            <Button
              // onClick={handleCreate}
              type="submit"
              variant="contained"
              size="large"
              color="secondary"
              // disabled={checkFields()}
            >
              {t('action.save')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {data?.length ? (
        <>
          {/* {data[0]?.status === ApplicationStatus.CONFIRMED ||
          data[0]?.status === ApplicationStatus.DECLINED ||
          data[0]?.status === ApplicationStatus.FAILED ||
          data[0]?.status === ApplicationStatus.PASSED ? ( */}
          <>
            <Box display={'flex'} justifyContent={'flex-end'}>
              {/* <Button
                onClick={() => handleClickOpen(CRUD_TYPE.CREATE)}
                variant="outlined"
                sx={{
                  mb: 2,
                }}
                color="primary"
              >
                <PlusIcon /> &nbsp;
                {t('create')}
              </Button> */}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={1}>
                <Typography fontSize={16} fontWeight={'bold'} p={2}>
                  №
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography fontSize={16} fontWeight={'bold'} p={2}>
                  {t('date')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={'bold'} p={2}></Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={'bold'} py={2} textAlign={'center'}>
                  {t('status')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography fontSize={16} fontWeight={'bold'} p={2}>
                  {t('speciality')}
                </Typography>
              </Grid>
            </Grid>
          </>
          {/* } */}
        </>
      ) : (
        <Box display={'flex'} justifyContent={'flex-end'}>
          {/* <Button
            onClick={() => handleClickOpen(CRUD_TYPE.CREATE)}
            variant="contained"
            sx={{
              mb: 2,
            }}
            color="secondary"
          >
            <PlusIcon /> &nbsp;
            {t('create')}
          </Button> */}
        </Box>
      )}
      <Box>
        {data?.map((d) => (
          <Accordion
            expanded={expanded === d.id?.toString()}
            onChange={handleChange(d.id ? d.id.toString() : '')}
            key={d.id}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel4bh-content"
              id="panel4bh-header"
            >
              <>
                <Grid container spacing={2}>
                  <Grid item xs={2}>
                    <Box display={'flex'}>
                      <Typography fontSize={16} width={70} mr={2}>
                        {d.id}
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {d.uid}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography fontSize={16}>{dateFormat(d.createdAt || '')}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontSize={16}>
                      {d.appType === '1' ? (
                        <span>{t('Врачлар ва фармацевтлар')}</span>
                      ) : d.appType === '2' ? (
                        <span>
                          {t(
                            '“Олий ҳамширалик иши” мутахассислигига эга бўлган ҳамширалар, ўрта тиббиёт ва ўрта фармацевтика ходимлари'
                          )}
                        </span>
                      ) : (
                        <span>{t('Бирламчи тиббиёт ходимлари')}</span>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      my={'auto'}
                    >
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        px={2}
                        py={1}
                        borderRadius={50}
                        sx={{ fontSize: 14 }}
                        bgcolor={COLORS[d.status]}
                        justifyContent={'center'}
                        color={'#fff'}
                      >
                        {t('status')}: &nbsp;{' '}
                        <Typography
                          fontSize={14}
                          fontWeight={'bold'}
                          textTransform={'uppercase'}
                          ml={1}
                        >
                          {t('appealStatus.' + d?.status)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontSize={16}>
                      {
                        specialities?.filter((c) => c.id === d.specialization)[0]?.title[
                          i18n.language as _Locale
                        ]
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </>
            </AccordionSummary>
            <AccordionDetails>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'}>
                <Box sx={{ width: '70%' }}>
                  <TableContainer component={Paper}>
                    <Table
                      sx={{ minWidth: 650, tableLayout: 'fixed' }}
                      aria-label="simple table"
                      size="small"
                    >
                      <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell
                            sx={{ fontWeight: '600', textAlign: 'center', fontSize: 18 }}
                            colSpan={2}
                          >
                            I. {t('Малакасига оид маълумотлар')}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('profile.phone')}</TableCell>
                          <TableCell>
                            <span>{d.indPhone}</span>
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('workplace')}</TableCell>
                          <TableCell>
                            <span>{d.workplace}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('position')}</TableCell>
                          <TableCell>
                            <span>{d.position}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('specialty')}</TableCell>
                          <TableCell>
                            {
                              specialities?.filter((c) => c.id === d.specializationId)[0]?.title[
                                i18n.language as _Locale
                              ]
                            }
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('workExperienceYear')}
                          </TableCell>
                          <TableCell>{d.workExperienceYear}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('specialtyWorkExperience')}
                          </TableCell>
                          <TableCell>{d.specialtyWorkExperience}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('claimedQualification')}
                          </TableCell>
                          <TableCell>
                            <span>
                              {t(
                                ['Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][
                                  Number(d.claimedQualification) - 1
                                ]
                              )}
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('electronicWorkBook')}
                          </TableCell>
                          <TableCell>
                            {d.electronicWorkBook ? (
                              <Button
                                onClick={() => onDownload(d.electronicWorkBook, d.appFrom)}
                                variant="contained"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                        {/* II */}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell
                            sx={{ fontWeight: '600', textAlign: 'center', fontSize: 18 }}
                            colSpan={2}
                          >
                            II.{' '}
                            {t('Профессионал таълим ташкилотларида ўқиганлигини тасдиқловчи архив')}
                          </TableCell>
                        </TableRow>
                        {/* <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('hisSpecialty')}</TableCell>
                          <TableCell>
                            <span>{d.hisSpecialty}</span>
                          </TableCell>
                        </TableRow> */}
                        {/* <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('nameMedicalEducational')}
                          </TableCell>
                          <TableCell>
                            <span>{d.nameMedicalEducational}</span>
                          </TableCell>
                        </TableRow> */}
                        {/* {d.presentation && d.presentation !== '' ? (
                          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('presentation')}</TableCell>
                            <TableCell>
                              <Button
                                onClick={() => onDownload(d.presentation)}
                                variant="contained"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ) : null} */}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell
                            sx={{ fontWeight: '600', textAlign: 'center', fontSize: 18 }}
                            colSpan={2}
                          >
                            III. {t('Амалдаги малака тоифаси: (агар мавжуд бўлса)')}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('qualificationCategory')}
                          </TableCell>
                          <TableCell>
                            {t(
                              ['attestation.absent', 'Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][
                                Number(d.qualificationCategory)
                              ]
                            )}
                          </TableCell>
                        </TableRow>
                        {d.qualificationCategory !== '0' ? (
                          <>
                            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell scope="row" sx={{ fontWeight: 'bold', width: '50%' }}>
                                {t('qualificationCategoryIssued')}
                              </TableCell>
                              <TableCell>
                                <span>{d.qualificationCategoryIssued}</span>
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell scope="row" sx={{ fontWeight: 'bold', width: '50%' }}>
                                {t('qualificationCategorySpecialty')}
                              </TableCell>
                              <TableCell>
                                <span>{d.qualificationCategorySpecialty}</span>
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell scope="row" sx={{ fontWeight: 'bold', width: '50%' }}>
                                {t('copyQualificationCategory')}
                              </TableCell>
                              <TableCell>
                                {d.copyQualificationCategory ? (
                                  <Button
                                    onClick={() =>
                                      onDownload(d.copyQualificationCategory, d.appFrom)
                                    }
                                    variant="contained"
                                    color="primary"
                                    sx={{ p: '5px 10px' }}
                                  >
                                    <Download /> &nbsp;
                                    {t('action.download')}
                                  </Button>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          </>
                        ) : null}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell
                            sx={{ fontWeight: '600', textAlign: 'center', fontSize: 18 }}
                            colSpan={2}
                          >
                            IV. {t('Кейинги 5 йил давомида малака оширганлиги')}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('creditAccumulated')}
                          </TableCell>
                          <TableCell>
                            <span>{d.creditAccumulated}</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('copyCertificateQualification')}
                          </TableCell>
                          <TableCell>
                            {d.copyCertificateQualification ? (
                              <Button
                                onClick={() =>
                                  onDownload(d.copyCertificateQualification, d.appFrom)
                                }
                                variant="contained"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('copyDiploma')}</TableCell>
                          <TableCell>
                            {d.copyDiploma ? (
                              <Button
                                onClick={() => onDownload(d.copyDiploma, d.appFrom)}
                                variant="contained"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t(
                              'copyDocTraining',
                              'Xorijiy davlatlarda olingan hujjatlarning nusxasi (mavjud bo‘lsa: ilmiy seminarlar, syezdlar, konferensiyalarda qatnashganligi hamda malaka oshirganligini tasdiqlovchi diplom, guvohnoma, sertifikat va boshqalar)'
                            )}
                          </TableCell>
                          <TableCell>
                            {d.copyDocTraining ? (
                              <Button
                                onClick={() => onDownload(d.copyDocTraining, d.appFrom)}
                                variant="contained"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell scope="row" sx={{ fontWeight: 'bold', width: '80%' }}>
                            {t('commission')}
                          </TableCell>
                          <TableCell>
                            {
                              commissions?.filter((c) => c.id === d.commissionId)[0]?.title[
                                i18n.language as _Locale
                              ]
                            }
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {t('Талабгорнинг фаолият юритаётган худуди')}
                          </TableCell>
                          <TableCell>
                            {
                              regions?.filter((c) => c.id === d.regionId)[0]?.title[
                                i18n.language as _Locale
                              ]
                            }
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  flexDirection={'column'}
                  mt={4}
                  sx={{ width: '20%', mx: '5%' }}
                  borderRadius={2}
                  textAlign={'center'}
                >
                  {d.reason ? (
                    <Box display={'flex'} mt={2} p={1}>
                      <Typography fontWeight={'bold'}>{t('description')}:</Typography>
                      <Typography ml={2} textAlign={'left'}>
                        {d.reason}
                      </Typography>
                    </Box>
                  ) : null}
                  {d.status !== ApplicationStatus.REJECTED_FOR_REVIEW &&
                  d.status !== ApplicationStatus.PERMISSION_TO_PAYMENT &&
                  d.invoice ? (
                    <Button
                      onClick={() => onDownload(d?.invoice, d.appFrom)}
                      variant="outlined"
                      color="primary"
                      sx={{ p: '5px 10px' }}
                    >
                      <Download /> &nbsp;
                      {t('Invoice')}
                    </Button>
                  ) : null}
                  {d.status === ApplicationStatus.REJECTED_FOR_REVIEW ? (
                    <Box display={'flex'} flexDirection={'column'}>
                      <Box>
                        {t('action.edit')}: &nbsp;
                        <Button
                          variant="outlined"
                          size="medium"
                          onClick={() => {
                            handleClickOpen(CRUD_TYPE.UPDATE, d);
                          }}
                        >
                          <Edit />
                        </Button>
                      </Box>
                    </Box>
                  ) : null}
                  {d.status === ApplicationStatus.PERMISSION_TO_PAYMENT ? (
                    <>
                      <InputFileUpload
                        getFileValue={getFileValue}
                        field="invoice"
                        justifyContent="center"
                        fileName={item.invoice}
                        // id={d?.id || ''}
                      />
                    </>
                  ) : null}
                  <br />
                  <br />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      {data?.length ? (
        <Box display={'flex'} justifyContent={'center'} my={3}>
          <Pagination
            count={total}
            variant="outlined"
            shape="rounded"
            onChange={(event, pageNumber) => pageChangeHandler(event, pageNumber)}
          />
        </Box>
      ) : null}
    </>
  );
}
